/**
 * Deterministic match replays.
 *
 * Live tables draw from a CSPRNG, so a replay cannot re-roll the same deals from a seed. Instead the
 * platform wraps its RNG in a {@link RecordingRng} and stores, with every logged step, the exact values
 * drawn while that step ran. {@link replay} feeds those values back through a {@link ReplayRng} and the
 * same (synchronous, deterministic) engine, reproducing every intermediate state exactly — without
 * ever storing per-step snapshots that contain hidden cards.
 */
import { EngineError, type AnyGameEngine, type EngineEvent, type SeatSetup } from './types.js';
import type { Rng } from './rng.js';

/** Wraps an RNG and remembers every value it returns until {@link take} is called. */
export class RecordingRng implements Rng {
  private draws: number[] = [];
  constructor(private readonly inner: Rng) {}
  int(maxExclusive: number): number {
    const v = this.inner.int(maxExclusive);
    this.draws.push(v);
    return v;
  }
  next(): number {
    const v = this.inner.next();
    this.draws.push(v);
    return v;
  }
  /** Returns the values drawn since the previous call and starts a new recording. */
  take(): number[] {
    const d = this.draws;
    this.draws = [];
    return d;
  }
}

/** Replays recorded draws in order; fails loudly if the engine asks for anything else. */
export class ReplayRng implements Rng {
  private pos = 0;
  constructor(private readonly draws: readonly number[]) {}
  private take(): number {
    if (this.pos >= this.draws.length) throw new ReplayError('Replay log is missing random draws');
    return this.draws[this.pos++]!;
  }
  int(maxExclusive: number): number {
    const v = this.take();
    if (!Number.isInteger(v) || v < 0 || v >= maxExclusive) throw new ReplayError('Replay draw out of range');
    return v;
  }
  next(): number {
    const v = this.take();
    if (!(v >= 0 && v < 1)) throw new ReplayError('Replay draw out of range');
    return v;
  }
  get exhausted(): boolean {
    return this.pos === this.draws.length;
  }
}

export class ReplayError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReplayError';
  }
}

export type ReplayEntry =
  | { kind: 'create'; config: unknown; seats: SeatSetup[]; draws: number[] }
  | { kind: 'action'; seat: number; action: unknown; draws: number[] }
  | { kind: 'system'; draws: number[] }
  | { kind: 'addSeat'; setup: SeatSetup }
  | { kind: 'removeSeat'; seat: number };

export interface ReplayFrame<V = unknown> {
  /** Index into the entry list that produced this frame. */
  index: number;
  entry: Omit<ReplayEntry, 'draws' | 'config'> & { kind: ReplayEntry['kind'] };
  events: EngineEvent[];
  view: V;
}

/**
 * Rebuilds a match from its log and returns one frame per step, each holding the redacted view for
 * `viewer` (null = spectator view; a function picks the seat per step, e.g. only while the viewer
 * occupied it). Throws {@link ReplayError} if the log is inconsistent.
 */
export function replay<V = unknown>(engine: AnyGameEngine, entries: readonly ReplayEntry[], viewer: number | null | ((index: number) => number | null)): ReplayFrame<V>[] {
  const first = entries[0];
  if (!first || first.kind !== 'create') throw new ReplayError('Replay log must start with a create entry');
  const frames: ReplayFrame<V>[] = [];
  let state: unknown;
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i]!;
    let events: EngineEvent[] = [];
    try {
      switch (e.kind) {
        case 'create': {
          if (i !== 0) throw new ReplayError('Unexpected create entry');
          const rng = new ReplayRng(e.draws);
          state = engine.createState(engine.normalizeConfig(e.config as never), e.seats, rng);
          if (!rng.exhausted) throw new ReplayError('Unused random draws (create)');
          break;
        }
        case 'action': {
          const rng = new ReplayRng(e.draws);
          events = engine.applyAction(state, e.seat, engine.parseAction(e.action), rng);
          if (!rng.exhausted) throw new ReplayError(`Unused random draws (step ${i})`);
          break;
        }
        case 'system': {
          const rng = new ReplayRng(e.draws);
          events = engine.runSystemStep(state, rng);
          if (!rng.exhausted) throw new ReplayError(`Unused random draws (step ${i})`);
          break;
        }
        case 'addSeat':
          if (!engine.addSeat) throw new ReplayError('Engine does not support seating');
          events = engine.addSeat(state, e.setup);
          break;
        case 'removeSeat':
          if (!engine.removeSeat) throw new ReplayError('Engine does not support seating');
          events = engine.removeSeat(state, e.seat).events;
          break;
      }
    } catch (err) {
      if (err instanceof EngineError) throw new ReplayError(`Step ${i} was rejected by the engine: ${err.message}`);
      throw err;
    }
    const { draws: _d, config: _c, ...rest } = e as ReplayEntry & { draws?: unknown; config?: unknown };
    frames.push({ index: i, entry: rest as ReplayFrame['entry'], events, view: engine.getView(state, typeof viewer === 'function' ? viewer(i) : viewer) as V });
  }
  return frames;
}
